// -> IConfigurationManager (ConfigurationController). UC-17..UC-20. One general reference-data
// mechanism (D-004) rather than per-vocabulary routes - see ConfigurationController.cs.
//
// FOUR OF THESE ARE READS THE ADMIN SCREENS ONCE HAD TO WORK AROUND, and the workarounds are worth
// naming because this file is where they lived. `listReferenceLists` closes D-233: the vocabulary
// editor's list-code box can be a closed picker rather than free text over a hand-maintained
// suggestion list. `listFieldMaps` and `getFieldMap` close D-234: the field-map panel was
// write-only and could only ever show a static "no active profile" prompt. `getBranding` closes
// D-235: the branding editor seeded its draft from `roadmapApi.getLive().branding`, which left it
// blank on a deployment's first day, when the roadmap read is the one thing that does not work yet.
//
// THE GATES ARE NOT ALL THE SAME AND A CLIENT CANNOT TELL BY LOOKING. `listReferenceLists` is
// Viewer-safe (`roadmap.view`, like `getReferenceList` and `getLayoutSettings`); `getBranding`,
// `listFieldMaps` and `getFieldMap` require `configuration.manage` and answer 403 to anybody else.
// Only the admin screens call the latter three, so the distinction never surfaces - but a future
// caller adding one of them to a Viewer-reachable screen will get a 403 rather than a blank panel.
import { http } from "./http";
import type {
  BrandingSaveRequest,
  BrandingTokens,
  FieldMapActivationRequest,
  FieldMapProfile,
  FieldMapProfileSummary,
  FieldMapSaveRequest,
  LayoutSettings,
  LayoutSettingsSaveRequest,
  ReferenceListDetail,
  ReferenceListSummary,
  ReferenceValue,
  ReferenceValueSaveRequest,
  RetirementImpact,
  SaveResult,
} from "./generated/contracts";

export const configurationApi = {
  listReferenceLists: () => http.get<ReferenceListSummary[]>("/api/config/reference-lists"),

  getReferenceList: (listCode: string, includeInactive = false) =>
    http.get<ReferenceListDetail>(`/api/config/reference-lists/${listCode}`, { query: { includeInactive } }),

  saveReferenceValue: (request: ReferenceValueSaveRequest) =>
    http.put<SaveResult<ReferenceValue>>("/api/config/reference-values", { body: request }),

  assessRetirement: (referenceValueKey: string) =>
    http.get<RetirementImpact>(`/api/config/reference-values/${referenceValueKey}/retirement-impact`),

  getLayoutSettings: () => http.get<LayoutSettings>("/api/config/layout"),

  saveLayoutSettings: (request: LayoutSettingsSaveRequest) =>
    http.put<SaveResult<LayoutSettings>>("/api/config/layout", { body: request }),

  getBranding: () => http.get<BrandingTokens>("/api/config/branding"),

  saveBranding: (request: BrandingSaveRequest) =>
    http.put<SaveResult<BrandingTokens>>("/api/config/branding", { body: request }),

  listFieldMaps: () => http.get<FieldMapProfileSummary[]>("/api/config/field-maps"),

  // 404 for a profile code this deployment does not hold, which arrives as an ApiError rather than
  // a null - the route deliberately does not confuse "no such profile" with "the deployment is
  // misconfigured", and a caller that wants the former should list first.
  getFieldMap: (profileCode: string) =>
    http.get<FieldMapProfile>(`/api/config/field-maps/${encodeURIComponent(profileCode)}`),

  saveFieldMap: (request: FieldMapSaveRequest) =>
    http.put<SaveResult<FieldMapProfile>>("/api/config/field-maps", { body: request }),

  // SAVING A PROFILE AND ACTIVATING ONE ARE TWO ROUTES BECAUSE THEY ARE TWO ACTS. A save stores a
  // mapping; this writes `source.activeProfile`, the setting the roadmap read, the importer, the
  // exporter and the publisher each resolve their source from - so after it, every one of them
  // reads through a different profile. A code this deployment holds no profile under comes back as
  // a 422 carrying ADMIN_FIELDMAP_UNKNOWN rather than being stored: an active-source setting
  // pointing at nothing makes every roadmap read a 500 (D-207).
  activateFieldMap: (request: FieldMapActivationRequest) =>
    http.put<SaveResult<FieldMapProfileSummary>>("/api/config/field-maps/active", { body: request }),
};
